import axios from "axios";
import * as token from "./token";
import url from 'url';

const proxy = url.parse(process.env.VUE_APP_QUOTAGUARDSTATIC_URL || '')

// Determina a URL base da API baseada no ambiente
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return '/api/v1/';
  } else {
    // Sempre usa a variável de ambiente em produção
    const baseUrl = process.env.VUE_APP_FLOWIT_URL;
    if (!baseUrl) {
      throw new Error('VUE_APP_FLOWIT_URL environment variable is required in production');
    }
    return `${baseUrl}/api/v1/`;
  }
};

// Determina a URL base para o partner client
const getPartnerBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return '/';
  } else {
    // Sempre usa a variável de ambiente em produção
    const baseUrl = process.env.VUE_APP_FLOWIT_URL;
    if (!baseUrl) {
      throw new Error('VUE_APP_FLOWIT_URL environment variable is required in production');
    }
    return `${baseUrl}/`;
  }
};

const client = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 50000,
  headers: { 
    "Application-Name": `${process.env.VUE_APP_APP_NAME || 'FlowIt'}Web`,
    ...(process.env.NODE_ENV !== 'development' && proxy.auth && { "Proxy-Authorization": "Basic " + (proxy.auth) })
  }
});

// Interceptor para adicionar token dinamicamente
client.interceptors.request.use(function(config) {
  // Evitar loop infinito nas rotas de autenticação
  if (config.url.includes('/sessions')) {
    const currentToken = token.get();
    if (currentToken) {
      config.headers.Authorization = currentToken;
    }
    return config;
  }

  // Para rotas normais, usar o token atual (o refresh será feito no response interceptor)
  const currentToken = token.get();
  if (currentToken) {
    config.headers.Authorization = currentToken;
  }
  
  return config;
});

// Interceptor para lidar com respostas de autenticação
client.interceptors.response.use(
  function(response) {
    return response;
  },
  function(error) {
    const originalRequest = error.config;

    // Função para verificar se é erro de sessão inválida
    const isSessionError = (error) => {
      if (!error.response) return false;
      
      // Erro 401 (não autorizado)
      if (error.response.status === 401) return true;
      
      // Erro 500 com mensagens que indicam sessão inválida
      if (error.response.status === 500) {
        const errorMessage = (error.response.data && error.response.data.message) || '';
        const errorString = JSON.stringify(error.response.data || {}).toLowerCase();
        
        // Detectar padrões que indicam token inválido
        const sessionErrorPatterns = [
          'invalid session token',
          'token expired',
          'unauthorized',
          'session expired',
          'authentication failed',
          'invalid token',
          'token not found'
        ];
        
        return sessionErrorPatterns.some(pattern => 
          errorMessage.toLowerCase().includes(pattern) || 
          errorString.includes(pattern)
        );
      }
      
      return false;
    };

    // Se detectou erro de sessão e não é uma tentativa de refresh
    if (isSessionError(error) && 
        !originalRequest._retry && 
        !originalRequest.url.includes('/sessions')) {
      
      console.warn('🔐 Session error detected:', {
        status: error.response && error.response.status,
        message: error.response && error.response.data && error.response.data.message,
        url: originalRequest.url
      });
      
      originalRequest._retry = true;

      // Importar refresh dinamicamente para evitar dependência circular
      return import('./refresh').then(function(refreshModule) {
        return refreshModule.refreshToken();
      }).then(function(newToken) {
        console.log('✅ Token refreshed successfully, retrying request');
        // Retry original request with new token
        originalRequest.headers.Authorization = newToken;
        return client(originalRequest);
      }).catch(function(refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        // Limpar tokens e redirecionar para login
        import('./token').then(function(tokenModule) {
          tokenModule.destroy();
        });
        
        if (window.location.pathname !== '/login') {
          console.log('🔄 Redirecting to login...');
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      });
    }

    return Promise.reject(error);
  }
);

const partnerClient = axios.create({
  baseURL: getPartnerBaseUrl(),
  timeout: 50000,
  headers: { 
    Authorization: "", 
    "Application-Name": `${process.env.VUE_APP_APP_NAME || 'FlowIt'}Web`,
    ...(process.env.NODE_ENV !== 'development' && proxy.auth && { "Proxy-Authorization": "Basic " + (proxy.auth) })
  }
});

export { client, partnerClient };
